// src/pages/Profile.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";

import { useAuth } from "../context/AuthContext";
import api, { showNotification, NOTIFICATIONTYPES } from "../utils/apiClient";
import {
  API_ENDPOINTS,
  STORAGE_KEYS,
  ROUTES,
  VALIDATION,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  APP_CONFIG,
} from "../utils/constants";

import {
  UserIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CameraIcon,
  XMarkIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  HomeIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";

import DeleteAccountButton from "../components/profile/DeleteAccountButton";

const Profile = () => {
  const { isAuthenticated, updateUserProfile } = useAuth();
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [showImageOptions, setShowImageOptions] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    alternateNumber: "",
    profile: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizeUser = (raw) => {
    if (!raw) return null;

    const id = raw.id ?? raw.user_id ?? raw.userid ?? raw.userId ?? null;

    return {
      id,
      name: raw.name ?? "",
      phoneNumber: raw.phoneNumber ?? raw.phone ?? "",
      email: raw.email ?? "",
      address: raw.address ?? "",
      alternateNumber:
        raw.alternateNumber ?? raw.alt_phone ?? raw.altphone ?? "",
      profile: raw.profile ?? raw.profile_pic ?? raw.profilePic ?? "",
      isDocumentVerified: raw.isDocumentVerified ?? raw.is_document_verified ?? 0,
    };
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    if (hasFetchedRef.current) return;

    loadProfile();

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadProfile = async () => {
    hasFetchedRef.current = true;
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) {
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      const resp = await api.get(API_ENDPOINTS.AUTH.PROFILE, {
        signal: abortControllerRef.current.signal,
      });

const raw = resp?.data?.data ?? null;   // user object
console.log("PROFILE resp =", resp);
     
const user = normalizeUser(raw);

      if (!user?.id) throw new Error(ERROR_MESSAGES.PROFILE_SERVICE_UNAVAILABLE);

      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      setProfileData(user);

      setFormData({
        name: user.name || "",
        address: user.address || "",
        alternateNumber: user.alternateNumber || "",
        profile: user.profile || "",
      });

      setPreviewImage(user.profile || null);

      if (updateUserProfile) updateUserProfile(user);

      setSuccess(SUCCESS_MESSAGES.PROFILE_LOADED);
    } catch (err) {
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;

      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.UNKNOWN_ERROR;

      if (status === 401 || status === 404) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        showNotification(ERROR_MESSAGES.SESSION_EXPIRED, NOTIFICATIONTYPES.ERROR);
        setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 1200);
        return;
      }

      setProfileData(null);
      setError(msg);
      showNotification(msg, NOTIFICATIONTYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };

    switch (fieldName) {
      case "name":
        if (!value?.trim()) errors.name = ERROR_MESSAGES.REQUIRED_FIELD;
        else if (value.trim().length < VALIDATION.NAME_MIN_LENGTH)
          errors.name = ERROR_MESSAGES.NAME_TOO_SHORT;
        else if (value.trim().length > VALIDATION.NAME_MAX_LENGTH)
          errors.name = ERROR_MESSAGES.NAME_TOO_LONG;
        else if (!VALIDATION.NAME_REGEX.test(value.trim()))
          errors.name = ERROR_MESSAGES.INVALID_NAME;
        else delete errors.name;
        break;

      case "alternateNumber":
        if (value?.trim() && !VALIDATION.PHONE_REGEX.test(value.trim()))
          errors.alternateNumber = ERROR_MESSAGES.INVALID_PHONE;
        else delete errors.alternateNumber;
        break;

      default:
        break;
    }

    setValidationErrors(errors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);

    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleCancelEdit = () => {
    if (!profileData) return;

    setFormData({
      name: profileData.name || "",
      address: profileData.address || "",
      alternateNumber: profileData.alternateNumber || "",
      profile: profileData.profile || "",
    });

    setPreviewImage(profileData.profile || null);
    setValidationErrors({});
    setError("");
    setSuccess("");
    setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = ERROR_MESSAGES.REQUIRED_FIELD;

    if (Object.keys(errors).length > 0 || Object.keys(validationErrors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...errors }));
      setError(ERROR_MESSAGES.VALIDATION_ERROR);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        name: formData.name?.trim() || null,
        address: formData.address?.trim() || null,
        alternateNumber: formData.alternateNumber?.trim() || null,
        // Note: backend updateProfile (shared earlier) does NOT update profile image URL.
        // Keep this in formData for UI, but only send if your backend supports it.
        // profile: formData.profile?.trim() || null,
      };

      const resp = await api.put(API_ENDPOINTS.AUTH.UPDATE_PROFILE, payload);
console.log("PROFILE resp =", resp);

      const raw = resp?.data?.data ?? null;
      const updated = normalizeUser(raw);

      if (!updated?.id) throw new Error(ERROR_MESSAGES.SERVER_ERROR);

      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      setProfileData(updated);
      setPreviewImage(updated.profile || null);

      setFormData({
        name: updated.name || "",
        address: updated.address || "",
        alternateNumber: updated.alternateNumber || "",
        profile: updated.profile || "",
      });

      setIsEditing(false);
      setSuccess(SUCCESS_MESSAGES.PROFILE_UPDATED);
      showNotification(SUCCESS_MESSAGES.PROFILE_UPDATED, NOTIFICATIONTYPES.SUCCESS);

      if (updateUserProfile) updateUserProfile(updated);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.SERVER_ERROR;
      setError(msg);
      showNotification(msg, NOTIFICATIONTYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleImageClick = () => setShowImageOptions(true);

  const handleImageOptionSelect = (type) => {
    setShowImageOptions(false);
    if (!fileInputRef.current) return;

    if (type === "camera") fileInputRef.current.setAttribute("capture", "environment");
    else fileInputRef.current.removeAttribute("capture");

    fileInputRef.current.click();
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!APP_CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showNotification(ERROR_MESSAGES.INVALID_IMAGE_FORMAT, NOTIFICATIONTYPES.ERROR);
      return;
    }

    try {
      setUploadingImage(true);

      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: "image/jpeg",
      });

      // Local preview immediately
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(compressedFile);

      // IMPORTANT:
      // Your constants show upload endpoints exist (API_ENDPOINTS.UPLOAD.PROFILEIMAGE),
      // but this Profile page earlier had a mismatch note.
      // If your backend upload route works, keep this. If not, remove upload block.
      const fd = new FormData();
      fd.append("profile", compressedFile, "profile.jpg");

      const uploadResp = await api.post(API_ENDPOINTS.UPLOAD.PROFILEIMAGE, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: APP_CONFIG.FILE_UPLOAD_TIMEOUT,
      });

      const imageUrl =
        uploadResp?.data?.profileImageUrl ||
        uploadResp?.data?.data?.profile ||
        uploadResp?.data?.data?.url ||
        uploadResp?.data?.url ||
        null;

      if (!imageUrl) throw new Error(ERROR_MESSAGES.UPLOAD_FAILED);

      setFormData((prev) => ({ ...prev, profile: imageUrl }));

      showNotification(SUCCESS_MESSAGES.AVATAR_UPDATED, NOTIFICATIONTYPES.SUCCESS);

      // If user is not editing, we still just update local UI (since backend updateProfile doesn't save profile url).
      // If you add backend support, call handleSaveProfile() here.
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.UPLOAD_FAILED;
      showNotification(msg, NOTIFICATIONTYPES.ERROR);
      setPreviewImage(profileData?.profile || null);
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const getVerificationBadge = (isVerified) => {
    if (isVerified === 1 || isVerified === true) {
      return (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <ShieldCheckIcon className="h-4 w-4" />
          <span>Verified</span>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-sm w-full">
          <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Unable to Load Profile
          </h3>
          <p className="text-sm text-gray-600 mb-4">Please try again.</p>
          <button
            onClick={() => {
              hasFetchedRef.current = false;
              loadProfile();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {error ? (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        ) : null}

        {success ? (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">{success}</p>
            </div>
          </div>
        ) : null}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="relative h-32 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="absolute inset-0 bg-black/5" />
          </div>

          <div className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-6 -mt-16">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-32 h-32 rounded-xl overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
                  {uploadingImage ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 gap-2">
                      <div className="w-8 h-8 border-[3px] border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                      <p className="text-xs text-gray-600">Uploading...</p>
                    </div>
                  ) : previewImage ? (
                    <img
                      src={previewImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600">
                      <UserIcon className="h-16 w-16 text-white" />
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleImageClick}
                  disabled={uploadingImage}
                  className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Add profile picture"
                >
                  <CameraIcon className="h-5 w-5" />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Title + actions */}
              <div className="flex-1 sm:pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {profileData.name || "Complete Your Profile"}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-4 w-4" />
                        <span>+91 {profileData.phoneNumber || "-"}</span>
                      </div>

                      {profileData.email ? (
                        <div className="flex items-center gap-1.5">
                          <EnvelopeIcon className="h-4 w-4" />
                          <span className="truncate max-w-[200px]">
                            {profileData.email}
                          </span>
                        </div>
                      ) : null}

                      {getVerificationBadge(profileData.isDocumentVerified)}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit Profile
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          onClick={handleSaveProfile}
                          disabled={saving || Object.keys(validationErrors).length > 0}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-4 w-4" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <IdentificationIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Personal Information
              </h2>
            </div>

            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Enter your full name"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing
                        ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20"
                        : "bg-gray-50 border-gray-200 text-gray-600"
                    } ${validationErrors.name ? "border-red-300" : ""}`}
                  />
                </div>
                {validationErrors.name ? (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.name}
                  </p>
                ) : null}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={profileData.phoneNumber ? `+91 ${profileData.phoneNumber}` : "-"}
                    disabled
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              </div>

              {/* Alternate number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternate Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="alternateNumber"
                    value={formData.alternateNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Enter 10 digit number"
                    maxLength={10}
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing
                        ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20"
                        : "bg-gray-50 border-gray-200 text-gray-600"
                    } ${validationErrors.alternateNumber ? "border-red-300" : ""}`}
                  />
                </div>
                {validationErrors.alternateNumber ? (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.alternateNumber}
                  </p>
                ) : null}
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <HomeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows={3}
                    placeholder="Enter your complete address"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                      isEditing
                        ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20"
                        : "bg-gray-50 border-gray-200 text-gray-600"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delete account (keep button) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-red-50 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Account Settings
              </h2>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              You can permanently delete your account. This action cannot be undone.
            </div>

            <div className="pt-4 border-t border-gray-100">
              <DeleteAccountButton />
            </div>
          </div>
        </div>

        {/* Upload modal */}
        {showImageOptions ? (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Choose Photo Source
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowImageOptions(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleImageOptionSelect("camera")}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    <CameraIcon className="h-5 w-5" />
                    Take Photo
                  </button>

                  <button
                    type="button"
                    onClick={() => handleImageOptionSelect("gallery")}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Choose from Gallery
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowImageOptions(false)}
                    className="w-full px-6 py-3.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Profile;
